import React, { useEffect, useState } from 'react'
import Image from 'next/image'
import { Container, Typography } from '@cleartrip/ct-design-components'
import {
    PE_EXPERIENCE_CARD_BACKGROUND,
    PE_EXPERIENCE_CARD_COPY,
    PE_EXPERIENCE_CARD_IMAGE,
    PE_EXPERIENCE_CARD_TOP_SCRIM,
    PE_EXPERIENCE_CARD_WIDTH_PX,
} from '@/constants/flights/premiumEconomy'
import { FARE_CLASS_CAROUSEL_IMAGES } from '@/constants/flights/peAssets'

type CarouselCabin = keyof typeof FARE_CLASS_CAROUSEL_IMAGES

/** Auto-advance interval for the cabin-image carousel. */
const CAROUSEL_INTERVAL_MS = 3000

/**
 * Per-cabin promo copy. The card sits on the cabin tab the user has already
 * switched to, so generic "upgrade" copy is stale there — it must state that
 * cabin's value props instead (CTAIR-2596). BE's
 * `cabinClassSummary[cabin].cabinPromoBanner.title` overrides these when present,
 * mirroring flights-pwa's `FareClassBanner`.
 */
const CABIN_CARD_COPY: Record<CarouselCabin, string> = {
    PREMIUM_ECONOMY: 'Get premium seats, complimentary meals & priority boarding',
    BUSINESS: 'Elevate your journey with business!',
    FIRST: 'Elevate your journey with first class!',
}

interface PEExperienceMediaCardProps {
    imageSrc?: string
    /** BE: `cabinClassSummary[cabin].cabinPromoBanner.title`. */
    title?: string
    showVideoModal: () => void
    /** Cabin whose (bundled) fallback carousel image set to show. */
    cabin?: CarouselCabin
    /**
     * BE-provided cabin media image URLs (cabinPromoBanner resources). Videos are
     * already filtered out upstream; used as the primary carousel source. When
     * empty (no BE media / all videos / fetch issue) we fall back to the bundled
     * cabin image set and finally the single promo image.
     */
    resourceImages?: string[]
}

const PEExperienceMediaCard: React.FC<PEExperienceMediaCardProps> = ({
    imageSrc = PE_EXPERIENCE_CARD_IMAGE,
    title,
    showVideoModal,
    cabin = 'PREMIUM_ECONOMY',
    resourceImages,
}) => {
    // Carousel image source priority: BE resources (videos already stripped) →
    // bundled cabin image set → single promo image.
    const fallbackCarousel = FARE_CLASS_CAROUSEL_IMAGES[cabin]
    const images = resourceImages?.length
        ? resourceImages
        : fallbackCarousel?.length
          ? fallbackCarousel
          : [imageSrc]
    const resolvedTitle = title || CABIN_CARD_COPY[cabin] || PE_EXPERIENCE_CARD_COPY
    const hasCarousel = images.length > 1
    const imagesKey = images.join('|')

    const [activeIndex, setActiveIndex] = useState(0)
    // Carousel srcs that failed to load — fall back to the single promo image.
    const [erroredSrcs, setErroredSrcs] = useState<Record<string, boolean>>({})

    // Reset when the image set changes (cabin switch or BE resources arriving).
    useEffect(() => {
        setActiveIndex(0)
        setErroredSrcs({})
    }, [imagesKey])

    useEffect(() => {
        if (!hasCarousel) return undefined
        const id = setInterval(() => {
            setActiveIndex((prev) => (prev + 1) % images.length)
        }, CAROUSEL_INTERVAL_MS)
        return () => clearInterval(id)
    }, [hasCarousel, images.length])

    return (
        <Container
            className="p-relative br-16 o-hidden ba-solid h-100p w-100p c-pointer"
            css={{
                borderColor: '#1A1A1A',
                background: PE_EXPERIENCE_CARD_BACKGROUND,
            }}
            onClick={showVideoModal}
        >
            {images.map((src, index) => {
                const resolvedSrc = erroredSrcs[src] ? imageSrc : src
                return (
                    <Image
                        key={src}
                        alt="Premium economy cabin"
                        src={resolvedSrc}
                        fill
                        sizes={`${PE_EXPERIENCE_CARD_WIDTH_PX}px`}
                        style={{
                            objectFit: 'cover',
                            objectPosition: '50% 100%',
                            opacity: index === activeIndex ? 1 : 0,
                            transition: 'opacity 600ms ease',
                        }}
                        onError={() => {
                            // Guard against the fallback re-triggering its own onError.
                            if (src === imageSrc) return
                            setErroredSrcs((prev) => (prev[src] ? prev : { ...prev, [src]: true }))
                        }}
                    />
                )
            })}
            <Container
                className="p-absolute t-0 l-0 r-0 disable-pointer-events"
                css={{ height: '121px', background: PE_EXPERIENCE_CARD_TOP_SCRIM }}
            />
            <Container
                className="p-absolute z-1"
                css={{ top: '22px', left: '16px', width: '188px' }}
            >
                <Typography variant="HM4" colorCode="#FFFFFF" css={{ lineHeight: '20px' }}>
                    {resolvedTitle}
                </Typography>
            </Container>
            {hasCarousel && (
                <Container
                    className="p-absolute flex flex-middle flex-center disable-pointer-events z-1"
                    css={{
                        left: 0,
                        right: 0,
                        bottom: '16px',
                        gap: '4px',
                    }}
                >
                    {images.map((src, index) => {
                        const isActive = index === activeIndex
                        return (
                            <Container
                                key={src}
                                css={{
                                    width: isActive ? '5px' : '4px',
                                    height: isActive ? '5px' : '4px',
                                    borderRadius: '24px',
                                    background: '#FFFFFF',
                                    opacity: isActive ? 1 : 0.7,
                                    transition:
                                        'opacity 300ms ease, width 300ms ease, height 300ms ease',
                                }}
                            />
                        )
                    })}
                </Container>
            )}
        </Container>
    )
}

export default PEExperienceMediaCard
